import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  actionCloseLaborTicketModal,
  actionSetRecentActivitySets,
  actionSetRecentOperatorRuns,
  actionSetCurrentWorkOrderOp,
  actionOptimisticActivityUpdate,
  actionOptimisticActivitySetUpdate,
  actionOpenClockOutModal,
  actionCanonicalActivitySwitch,
  actionOptimisticActivitySwitch,
  actionCanonicalActivityUpdate,
  actionCanonicalActivitySetUpdate,
  actionSetLaborTickets,
  actionSetEmbeddedLaborTicket,
} from 'lib/actions';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getCurrentLaborTicket } from 'lib/selectors/getLaborTickets';
import { getLaborTicketAction } from 'lib/selectors/getLaborTicketAction';
import { getLaborTicketModalOpen } from 'lib/selectors/getLaborTicketModalOpen';
import { getIsAuthorizationModalOpen } from 'lib/selectors/getIsAuthorizationModalOpen';
import {
  MMEmbeddableZone,
  MMEmbeddableZoneProvider,
  useEmbeddedEffect,
} from '@machinemetrics/mm-react-embeddable';
import { LABOR_TICKET_HOST } from 'lib/appConfig';
import i18n from 'i18next';

import { getEmbeddedLaborTicket } from 'lib/selectors/getEmbeddedLaborTicket';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { getLatestActivityRef } from 'lib/selectors/getLatestActivityRef';

import { getMachine } from 'lib/selectors/getMachine';
import { getUnattendedOperatorPersonId } from 'lib/selectors/getUnattendedOperatorPersonId';
import { getEnableOperatorVerification } from 'lib/selectors/getEnableOperatorVerification';
import { getLaborScopeTotalParts } from 'lib/selectors/getScopeTotalParts';
import PersistentDrawer, {
  PersistentDrawerBody,
  PersistentDrawerHeader,
  PersistentDrawerCloseButton,
} from '../PersistentDrawer';

const EmbeddedLaborTicket = ({ id, path, personId }) => {
  const dispatch = useDispatch();
  const laborTicketAction = useSelector(getLaborTicketAction);
  const latestActivitySet = useSelector(getLatestActivitySet);
  const machine = useSelector(getMachine);
  const latestActivityRef = useSelector(getLatestActivityRef);
  const unattendedOperatorPersonId = useSelector(getUnattendedOperatorPersonId);
  const enableOperatorVerification = useSelector(getEnableOperatorVerification);
  const currentOperator = useSelector(getCurrentOperator);
  const laborScopeTotalParts = useSelector(getLaborScopeTotalParts);
  const laborTicketModalOpen = useSelector(getLaborTicketModalOpen);

  useEmbeddedEffect(id, 'labor-ticket-form:close', () => {
    dispatch(actionCloseLaborTicketModal());
  });

  // No longer refresh on not-found; the embedded app will close itself

  useEmbeddedEffect(id, 'labor-ticket-form:submit', (data) => {
    const {
      updateActivitySet,
      stopOperatorRun,
      pauseSetup,
      completeSetup,
      closeLaborTicket,
      startOperatorRun,
    } = data;

    // Capture the current operator BEFORE any state changes happen
    const operatorBeforeSubmit = currentOperator;

    if (closeLaborTicket?.personId === unattendedOperatorPersonId) {
      dispatch(actionSetLaborTickets([]));
      dispatch(actionSetEmbeddedLaborTicket(null));
    }

    if (updateActivitySet) {
      dispatch(actionSetRecentActivitySets([updateActivitySet.activitySet]));
      if (updateActivitySet.activitySet.closedAt) {
        dispatch(actionSetCurrentWorkOrderOp(null));
      }
    }

    if (startOperatorRun) {
      const { operator, machine: runMachine, ...rest } = startOperatorRun;
      dispatch(
        actionSetRecentOperatorRuns({
          operatorRuns: [
            {
              ...rest,
              operatorId: operator.id,
              machineId: runMachine.id,
            },
          ],
        })
      );
    }

    if (stopOperatorRun) {
      const { operator, ...rest } = stopOperatorRun;
      dispatch(
        actionSetRecentOperatorRuns({
          operatorRuns: [
            { ...rest, operatorId: operator.id, machineId: machine.id },
          ],
        })
      );
    }

    if (pauseSetup) {
      // Update the individual activity with the new endAt time
      dispatch(
        actionOptimisticActivityUpdate({
          activity: {
            activityRef: pauseSetup.activityRef,
            endAt: pauseSetup.endAt,
            end: pauseSetup.endAt,
            activitySetRef: latestActivitySet.activitySetRef,
          },
        })
      );
      dispatch(
        actionCanonicalActivityUpdate({
          activity: {
            ...pauseSetup,
            end: pauseSetup.endAt,
          },
          activityRef: pauseSetup.activityRef,
        })
      );
      dispatch(
        actionOptimisticActivitySetUpdate({
          activitySet: {
            ...latestActivitySet,
            updatedAt: pauseSetup.endAt,
          },
        })
      );
      dispatch(
        actionCanonicalActivitySetUpdate({
          activitySet: {
            ...latestActivitySet,
            updatedAt: pauseSetup.endAt,
          },
          activitySetRef: latestActivitySet.activitySetRef,
        })
      );
    }

    if (completeSetup) {
      // Update the individual activity with the full response from the API
      dispatch(
        actionOptimisticActivitySwitch({
          optimisticActivity: {
            ...completeSetup,
            start: completeSetup.startAt,
            end: completeSetup.endAt,
          },
          latestActivityRef,
        })
      );

      dispatch(
        actionCanonicalActivitySwitch({
          activity: {
            ...completeSetup,
            start: completeSetup.startAt,
            end: completeSetup.endAt,
          },
          activityRef: completeSetup.activityRef,
          optimisticAcitivityRef: null,
        })
      );
    }
    if (stopOperatorRun && stopOperatorRun.machine.id === machine.id) {
      // Regular sign out - the operator info is in stopOperatorRun
      dispatch(actionOpenClockOutModal());
    }

    if (startOperatorRun && startOperatorRun.machineId === machine.id) {
      // Unattended operator starting - use the operator from before submit
      // operatorBeforeSubmit should be the actual operator who submitted the form
      dispatch(actionOpenClockOutModal(operatorBeforeSubmit?.erpId));
    }

    dispatch(actionCloseLaborTicketModal());
    dispatch(actionSetEmbeddedLaborTicket(null));
  });

  return (
    <MMEmbeddableZone
      id={id}
      src={`${LABOR_TICKET_HOST}`}
      path={path}
      context={{
        personId,
        action: laborTicketAction,
        enableOperatorVerification,
        opened: laborTicketModalOpen,
        laborScopeTotalPartsByMachine: machine?.machineRef
          ? { [machine.machineRef]: laborScopeTotalParts }
          : {},
      }}
      params={{
        language: i18n.language,
      }}
    />
  );
};

const LaborTicketWrapper = () => {
  const dispatch = useDispatch();
  const id = 'labor-ticket-form';
  const embeddedLaborTicket = useSelector(getEmbeddedLaborTicket);
  const currentLaborTicket = useSelector(getCurrentLaborTicket);
  const laborTicketFormOpen = useSelector(getLaborTicketModalOpen);
  const authorizationModalOpen = useSelector(getIsAuthorizationModalOpen);

  const laborTicket = embeddedLaborTicket || currentLaborTicket;
  const laborTicketRef = laborTicket?.laborTicketRef;
  const path = laborTicketRef ? `/labor-ticket/${laborTicketRef}` : null;

  useEffect(() => {
    if (authorizationModalOpen) {
      dispatch(actionCloseLaborTicketModal());
    }
  }, [authorizationModalOpen, dispatch]);

  if (!laborTicket) {
    return null;
  }

  return (
    <PersistentDrawer
      isOpen={laborTicketFormOpen}
      onClose={() => {
        return dispatch(actionCloseLaborTicketModal());
      }}
      placement="bottom"
      size="full"
    >
      <PersistentDrawerHeader>
        Confirm work produced
        <PersistentDrawerCloseButton />
      </PersistentDrawerHeader>
      <PersistentDrawerBody
        style={{
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          margin: 0,
        }}
      >
        <MMEmbeddableZoneProvider>
          <EmbeddedLaborTicket
            id={id}
            path={path}
            personId={laborTicket.personId}
          />
        </MMEmbeddableZoneProvider>
      </PersistentDrawerBody>
    </PersistentDrawer>
  );
};

export default LaborTicketWrapper;
